import { CycleTimeImprovement } from '../contexts/types';
import GoogleAPI from '../utils/GoogleAPI';
import { isValidYearMonth, isDateInRange, padYearMonth } from '../utils/utils';
import i18n from '../utils/i18n';

const T_PREFIX = 'features.InvestmentScreening.CycleTimeReduction.spreadsheet';

/**
 * サイクルタイム短縮データ変換結果
 */
export interface CycleTimeReductionConversionResult {
  data: CycleTimeImprovement[];
  errors: string[];
}

/**
 * サイクルタイム短縮データをSpreadSheetデータに変換
 * @param list サイクルタイム短縮リスト
 * @returns SpreadSheet用の2次元配列
 */
export const cycleTimeReductionToSpreadSheetData = (list: CycleTimeImprovement[]): any[][] => {
  // ヘッダー行（1行目）
  const header = [i18n.t(`${T_PREFIX}.header.month`), i18n.t(`${T_PREFIX}.header.cycleTime`)];

  // データ行（2行目から）
  const dataRows = list.map(item => [
    padYearMonth(item.month),
    item.cycleTime,
  ]);

  return [header, ...dataRows];
};

/**
 * SpreadSheetデータをサイクルタイム短縮データに変換
 * SpreadSheetの行をそのまま新規データとして構築する（既存データは破棄）
 * 不正な期間・値の行はスキップされる
 * @param values SpreadSheetから取得した2次元配列（ヘッダー行を含まない、A2:B から取得）
 * @param rangeStart 期間の範囲開始日（量産開始日）。指定時、範囲外の行はスキップ
 * @param rangeEnd 期間の範囲終了日（EOL予定日）。指定時、範囲外の行はスキップ
 * @returns 変換結果
 */
export const spreadSheetDataToCycleTimeReduction = (
  values: any[][],
  rangeStart?: string | null,
  rangeEnd?: string | null,
): CycleTimeReductionConversionResult => {
  const emptyRow: CycleTimeImprovement = { month: "", cycleTime: 0, cycleTimeText: "0.0" };

  if (!values || values.length === 0) {
    return { data: [emptyRow], errors: [] };
  }

  const result: CycleTimeImprovement[] = [];
  const errors: string[] = [];

  for (let i = 0; i < values.length; i++) {
    const row = values[i];
    if (!row || row.length === 0) continue;

    // A列(index 0): 期間 - 不正な日付はスキップ
    const monthRaw = row[0]?.toString().trim() ?? "";
    const month = isValidYearMonth(row[0]);
    if (!month) {
      if (monthRaw) {
        errors.push(i18n.t(`${T_PREFIX}.message.invalidMonth`, { row: i + 1, value: monthRaw }));
      }
      continue;
    }

    // 期間の範囲チェック（量産開始日〜EOL予定日）
    if (!isDateInRange(month, rangeStart, rangeEnd)) {
      errors.push(i18n.t(`${T_PREFIX}.message.monthOutOfRange`, { row: i + 1, month, rangeStart, rangeEnd }));
      continue;
    }

    // B列(index 1): サイクルタイム短縮(秒) - 0〜999の範囲チェック、不正な値はスキップ
    const cycleTimeRaw = row[1]?.toString().trim() ?? "";
    const cycleTime = parseFloat(row[1]);
    if (isNaN(cycleTime)) {
      errors.push(i18n.t(`${T_PREFIX}.message.invalidCycleTime`, { row: i + 1, value: cycleTimeRaw }));
      continue;
    }
    if (cycleTime < 0 || cycleTime > 999) {
      errors.push(i18n.t(`${T_PREFIX}.message.cycleTimeOutOfRange`, { row: i + 1, value: cycleTimeRaw }));
      continue;
    }

    result.push({
      month,
      cycleTime,
      cycleTimeText: String(cycleTime),
    });
  }

  // 有効な行がない場合は空データ1行を返す（画面入力を可能にするため）
  return { data: result.length > 0 ? result : [emptyRow], errors };
};

/**
 * サイクルタイム短縮シートの表示書式・背景色を設定
 * @param googleAPI GoogleAPIインスタンス
 * @param spreadsheetId SpreadSheet ID
 * @param sheetId シートID
 */
export const setupCycleTimeReductionSheet = async (
  googleAPI: GoogleAPI,
  spreadsheetId: string,
  sheetId: number,
): Promise<void> => {
  // 新規行追加に備えて50行分の書式を適用
  const FORMAT_ROW_COUNT = 50;

  // A列: 期間の表示形式をyyyy-mmに設定
  const dateFormat = { numberFormat: { type: "DATE" as const, pattern: "yyyy-mm" } };
  // B列: サイクルタイム短縮(秒)の表示形式を小数点1桁に設定
  const numberFormat = { numberFormat: { type: "NUMBER" as const, pattern: "0.0" } };
  await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
    { sheetId, startRow: 2, endRow: 1 + FORMAT_ROW_COUNT, startCol: 1, endCol: 1, format: dateFormat },
    { sheetId, startRow: 2, endRow: 1 + FORMAT_ROW_COUNT, startCol: 2, endCol: 2, format: numberFormat },
  ]);

  // ヘッダー行に薄い灰色の背景色を設定
  const readOnlyBgColor = { red: 0.93, green: 0.93, blue: 0.93 };
  await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [
    { sheetId, startRow: 1, endRow: 1, startCol: 1, endCol: 2, format: { backgroundColor: readOnlyBgColor } },
  ]);
};
